import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { MessageSquare, Lightbulb, Bug, User, Mail, Clock, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../../App';

export default function FeedbackManagement() {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', type: '' });
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    fetchFeedback();
  }, [filter]);

  const fetchFeedback = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (filter.status) params.append('status', filter.status);
      if (filter.type) params.append('type', filter.type);

      const response = await axios.get(`${API}/admin/feedback?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setFeedback(response.data.feedback || []);
    } catch (error) {
      toast.error('Failed to fetch feedback');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (feedbackId, status) => {
    setUpdatingStatus(true);
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API}/admin/feedback/${feedbackId}/status`,
        null,
        {
          params: { status, admin_notes: adminNotes },
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      toast.success('Status updated successfully');
      setSelectedFeedback(null);
      setAdminNotes('');
      fetchFeedback();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'feedback':
        return <MessageSquare className="w-5 h-5 text-blue-600" />;
      case 'feature_request':
        return <Lightbulb className="w-5 h-5 text-purple-600" />;
      case 'bug_report':
        return <Bug className="w-5 h-5 text-red-600" />;
      default:
        return <MessageSquare className="w-5 h-5" />;
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { label: 'New', className: 'bg-blue-100 text-blue-700' },
      reviewed: { label: 'Reviewed', className: 'bg-yellow-100 text-yellow-700' },
      in_progress: { label: 'In Progress', className: 'bg-purple-100 text-purple-700' },
      resolved: { label: 'Resolved', className: 'bg-green-100 text-green-700' },
      closed: { label: 'Closed', className: 'bg-gray-100 text-gray-700' }
    };
    
    const config = statusConfig[status] || statusConfig.new;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold">{feedback.length}</div>
            <p className="text-sm text-gray-600">Total Submissions</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-blue-600">
              {feedback.filter(f => f.status === 'new').length}
            </div>
            <p className="text-sm text-gray-600">New</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-purple-600">
              {feedback.filter(f => f.status === 'in_progress').length}
            </div>
            <p className="text-sm text-gray-600">In Progress</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-2xl font-bold text-green-600">
              {feedback.filter(f => f.status === 'resolved').length}
            </div>
            <p className="text-sm text-gray-600">Resolved</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-4">
            <div className="flex-1">
              <Label>Status</Label>
              <select
                value={filter.status}
                onChange={(e) => setFilter({ ...filter, status: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">All Statuses</option>
                <option value="new">New</option>
                <option value="reviewed">Reviewed</option>
                <option value="in_progress">In Progress</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div className="flex-1">
              <Label>Type</Label>
              <select
                value={filter.type}
                onChange={(e) => setFilter({ ...filter, type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
              >
                <option value="">All Types</option>
                <option value="feedback">Feedback</option>
                <option value="feature_request">Feature Request</option>
                <option value="bug_report">Bug Report</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Feedback List */}
      <Card>
        <CardHeader>
          <CardTitle>Feedback & Feature Requests</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading...</div>
          ) : feedback.length === 0 ? (
            <div className="text-center py-8 text-gray-500">No feedback found</div>
          ) : (
            <div className="space-y-4">
              {feedback.map((item) => (
                <div
                  key={item.id}
                  className="p-4 border rounded-lg hover:shadow-md transition cursor-pointer"
                  onClick={() => setSelectedFeedback(item)}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-start gap-3">
                      {getTypeIcon(item.type)}
                      <div>
                        <h3 className="font-semibold text-lg">{item.subject}</h3>
                        <div className="flex items-center gap-3 text-sm text-gray-600 mt-1">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {item.user?.full_name || 'Unknown'}
                          </span>
                          <span className="flex items-center gap-1">
                            <Mail className="w-3 h-3" />
                            {item.user?.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(item.created_at).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="capitalize">{item.priority}</Badge>
                      {getStatusBadge(item.status)}
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 mb-3">{item.description}</p>
                  {item.admin_notes && (
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-sm font-medium text-blue-900">Admin Notes:</p>
                      <p className="text-sm text-blue-800">{item.admin_notes}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Update Status Modal */}
      {selectedFeedback && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Update Feedback Status</CardTitle>
                <Button variant="ghost" onClick={() => setSelectedFeedback(null)}>×</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="font-semibold mb-1">{selectedFeedback.subject}</h3>
                <p className="text-sm text-gray-600">{selectedFeedback.description}</p>
              </div>

              <div>
                <Label>Update Status</Label>
                <div className="grid grid-cols-5 gap-2 mt-2">
                  {['new', 'reviewed', 'in_progress', 'resolved', 'closed'].map((status) => (
                    <Button
                      key={status}
                      variant={selectedFeedback.status === status ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => updateStatus(selectedFeedback.id, status)}
                      disabled={updatingStatus}
                      className="capitalize"
                    >
                      {status.replace('_', ' ')}
                    </Button>
                  ))}
                </div>
              </div>

              <div>
                <Label>Admin Notes (Optional)</Label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Add notes for the user..."
                  rows={4}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
